import { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
  Box,
  Card,
  CardContent,
  Divider,
  TextField,
  Button,
  Typography,
  Alert,
  Container,
  Paper,
  IconButton,
  InputAdornment,
  Link,
  CircularProgress,
} from '@mui/material'
import { Visibility, VisibilityOff, VpnKey } from '@mui/icons-material'
import { authApi, tenantApi } from '../services/api'
import { loginUser, clearError, loginSuccess } from '../store/authSlice'
import { AppDispatch, RootState } from '../store'

interface LoginPageProps {
  onSwitchToSignup?: () => void
}

const LoginPage = ({ onSwitchToSignup }: LoginPageProps) => {
  const [credentials, setCredentials] = useState({
    username: '',
    password: '',
  })
  const [showPassword, setShowPassword] = useState(false)
  const [forgotMode, setForgotMode] = useState(false)
  const [resetMode, setResetMode] = useState(false)
  const [forgotEmail, setForgotEmail] = useState('')
  const [forgotMessage, setForgotMessage] = useState('')
  const [forgotError, setForgotError] = useState('')
  const [forgotLoading, setForgotLoading] = useState(false)
  const [resetToken, setResetToken] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [resetMessage, setResetMessage] = useState('')
  const [resetError, setResetError] = useState('')
  const [resetLoading, setResetLoading] = useState(false)
  // SSO state
  const [ssoMode, setSsoMode] = useState(false)
  const [ssoEmail, setSsoEmail] = useState('')
  const [ssoChecking, setSsoChecking] = useState(false)
  const [ssoError, setSsoError] = useState('')

  const dispatch = useDispatch<AppDispatch>()
  const { loading, error } = useSelector((state: RootState) => state.auth)

  /**
   * Handle the SSO callback's fragment-delivered JWT.
   *
   * After /sso/callback verifies the IdP response, the backend redirects
   * to FRONTEND_URL with the JWT in the URL fragment (#token=...). Putting
   * it in the fragment (not the query string) means the token never enters
   * server access logs or the Referer header.
   *
   * On mount we check for that fragment, decode the unsigned-side claims
   * to build the user shape the rest of the app expects, then dispatch
   * loginSuccess and bounce to /dashboard. Decoding without verifying is
   * safe here because the JWT was signed by our own backend in the same
   * request flow that issued the redirect.
   */
  useEffect(() => {
    const hash = window.location.hash || ''
    if (!hash.startsWith('#') || !hash.includes('token=')) return
    const params = new URLSearchParams(hash.slice(1))
    const token = params.get('token')
    if (!token) return
    try {
      const parts = token.split('.')
      if (parts.length !== 3) throw new Error('Malformed JWT')
      // base64url → base64 → JSON
      const b64 = parts[1].replace(/-/g, '+').replace(/_/g, '/')
      const pad = b64 + '='.repeat((4 - b64.length % 4) % 4)
      const payload = JSON.parse(atob(pad))
      const user = {
        user_id: payload.sub,
        username: payload.username || payload.email,
        email: payload.email,
        role: payload.role,
        tenant_id: payload.tenant_id,
        user_type: payload.type,
        subscription_status: payload.subscription_status,
        tenant_tier: payload.tenant_tier,
      }
      dispatch(loginSuccess({ token, user }))
      localStorage.setItem('token', token)
      localStorage.setItem('user', JSON.stringify(user))
      // Clear the fragment so a page refresh doesn't replay
      window.history.replaceState({}, '', window.location.pathname)
      // Navigate to dashboard. Using window.location.replace keeps history clean.
      window.location.replace('/dashboard')
    } catch (e) {
      setSsoError('SSO sign-in failed — the returned token was malformed. Try again or use password sign-in.')
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleSsoSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setSsoError('')
    if (!ssoEmail.trim() || !ssoEmail.includes('@')) {
      setSsoError('Enter a valid email address')
      return
    }
    setSsoChecking(true)
    try {
      const res = await tenantApi.resolveSsoTenant(ssoEmail.trim())
      const loginUrl = res.data?.login_url
      if (!loginUrl) throw new Error('No SSO login URL returned')
      // Bounce to /sso/login (handled by the tenant service). Build absolute URL
      // through the same origin so cookies + same-site behavior is preserved.
      // The login URL is relative (e.g., /sso/login?email=...).
      window.location.assign(loginUrl.startsWith('http') ? loginUrl : `/api/tenant${loginUrl}`)
    } catch (e: any) {
      if (e.response?.status === 404) {
        setSsoError("We couldn't find SSO configured for that email's domain. Use password sign-in below, or contact your tenant admin to configure SSO.")
      } else {
        setSsoError(e.response?.data?.detail || 'SSO lookup failed')
      }
      setSsoChecking(false)
    }
  }

  // Check for reset token in URL
  useState(() => {
    const params = new URLSearchParams(window.location.search)
    const token = params.get('reset_token')
    if (token) {
      setResetToken(token)
      setResetMode(true)
    }
  })

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault()
    setForgotError('')
    setForgotMessage('')
    setForgotLoading(true)
    try {
      const res = await authApi.forgotPassword(forgotEmail)
      setForgotMessage(res.data.message)
    } catch (err: any) {
      setForgotError(err.response?.data?.detail || 'Request failed. Please try again.')
    } finally {
      setForgotLoading(false)
    }
  }

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault()
    setResetError('')
    setResetMessage('')
    if (newPassword.length < 8) {
      setResetError('Password must be at least 8 characters')
      return
    }
    setResetLoading(true)
    try {
      const res = await authApi.resetPassword(resetToken, newPassword)
      setResetMessage(res.data.message)
      // Clear URL params
      window.history.replaceState({}, '', window.location.pathname)
      setTimeout(() => {
        setResetMode(false)
        setResetMessage('')
      }, 3000)
    } catch (err: any) {
      setResetError(err.response?.data?.detail || 'Reset failed. The link may have expired.')
    } finally {
      setResetLoading(false)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    dispatch(clearError())
    dispatch(loginUser(credentials))
  }

  const handleChange = (field: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setCredentials({ ...credentials, [field]: e.target.value })
  }

  return (
    <Container component="main" maxWidth="sm">
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
        }}
      >
        <Paper elevation={3} sx={{ p: 4 }}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              mb: 3,
            }}
          >
            <Box component="img" src="/brand/logo-horizontal.svg" alt="ARIAS" sx={{ height: 44, width: 'auto', display: 'block', mb: 1 }} />
            <Typography variant="subtitle1" color="text.secondary" sx={{ fontWeight: 500 }}>
              The Control Plane for AI Agents
            </Typography>
            <Typography variant="body2" sx={{ textAlign: 'center', mt: 1, mb: 2, fontStyle: 'italic', color: 'text.secondary' }}>
              We don&apos;t monitor agents after they drift.
              We catch the drift before they ship.
            </Typography>

            {/* The Problem */}
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'left', lineHeight: 1.8 }}>
              A prompt gets rewritten. A new tool is connected. A model gets upgraded.
              Small changes in agent code can lead to big changes in behavior &mdash;
              but most teams only find out after deployment.
            </Typography>

            {/* The 4-verb solution */}
            <Box sx={{ mt: 2.5, mb: 2 }}>
              {[
                { verb: 'Discover', desc: 'Find every AI agent across 28+ frameworks and 4 languages. Classify architecture patterns. Map capabilities.' },
                { verb: 'Assess', desc: 'Score governance maturity across six dimensions &mdash; from prompt quality to tool permissions to guardrail effectiveness.' },
                { verb: 'Certify', desc: 'Track each agent\u2019s behavioral identity. Detect the moment a prompt, tool, or model change alters what the agent can do.' },
                { verb: 'Prove', desc: 'Generate compliance evidence mapped to OWASP, EU AI Act, SOC 2, and more &mdash; automatically, from every scan.' },
              ].map(item => (
                <Box key={item.verb} sx={{ display: 'flex', gap: 1.5, mb: 1.5 }}>
                  <Typography variant="body2" sx={{ fontWeight: 700, minWidth: 65, color: 'primary.main' }}>
                    {item.verb}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}
                    dangerouslySetInnerHTML={{ __html: item.desc }} />
                </Box>
              ))}
            </Box>

            {/* How it fits */}
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'left', lineHeight: 1.8 }}>
              ARIAS runs in your CI/CD pipeline &mdash; GitHub Actions, GitLab CI,
              AWS CodeBuild, Azure Pipelines, or any other platform. The scanner runs
              on-premise. No source code ever leaves your environment.
            </Typography>

            <Divider sx={{ my: 2 }} />

            {/* Cross-functional value */}
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'left', lineHeight: 1.8 }}>
              Your security team needs to know what your agents can do.
              Your compliance team needs proof your policies are followed.
              Your leadership needs visibility into AI maturity.
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'left', lineHeight: 1.8, mt: 1 }}>
              Today, that means spreadsheets, manual reviews, and meetings.
              ARIAS generates the answers automatically &mdash; from every scan
              you already run. No extra work. No separate tools.
            </Typography>

            <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center', display: 'block', mt: 3, maxWidth: 500, mx: 'auto' }}>
              The ARIAS platform &mdash; including its Agent Behavioral Fingerprint methodology, behavioral drift detection and verdict system, privacy-preserving scanner architecture, and CI/CD governance gate &mdash; is patent pending technology, subject of a pending U.S. patent application.
            </Typography>

            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', fontWeight: 600, mt: 2 }}>
              Ship agents confidently. Every commit.
            </Typography>
          </Box>

          <Card>
            <CardContent>
              {/* Reset Password Mode */}
              {resetMode ? (<>
                <Typography variant="h6" component="h2" gutterBottom>Set New Password</Typography>
                {resetMessage && <Alert severity="success" sx={{ mb: 2 }}>{resetMessage}</Alert>}
                {resetError && <Alert severity="error" sx={{ mb: 2 }}>{resetError}</Alert>}
                <Box component="form" onSubmit={handleResetPassword}>
                  <TextField fullWidth label="New Password" margin="normal" required
                    type={showPassword ? 'text' : 'password'} value={newPassword}
                    onChange={e => setNewPassword(e.target.value)}
                    helperText="Minimum 8 characters"
                    InputProps={{ endAdornment: (
                      <InputAdornment position="end">
                        <IconButton onClick={() => setShowPassword(!showPassword)} edge="end" size="small">
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ) }}
                  />
                  <Button type="submit" fullWidth variant="contained" disabled={resetLoading} sx={{ mt: 3, mb: 2 }}>
                    {resetLoading ? 'Resetting...' : 'Reset Password'}
                  </Button>
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
                  <Link component="button" variant="body2" onClick={() => { setResetMode(false); setResetError(''); setResetMessage('') }} sx={{ cursor: 'pointer' }}>
                    Back to Sign In
                  </Link>
                </Typography>

              {/* Forgot Password Mode */}
              </>) : forgotMode ? (<>
                <Typography variant="h6" component="h2" gutterBottom>Reset Password</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Enter your email address and we&apos;ll send you a link to reset your password.
                </Typography>
                {forgotMessage && <Alert severity="success" sx={{ mb: 2 }}>{forgotMessage}</Alert>}
                {forgotError && <Alert severity="error" sx={{ mb: 2 }}>{forgotError}</Alert>}
                <Box component="form" onSubmit={handleForgotPassword}>
                  <TextField fullWidth label="Email" type="email" margin="normal" required autoFocus
                    value={forgotEmail} onChange={e => setForgotEmail(e.target.value)} />
                  <Button type="submit" fullWidth variant="contained" disabled={forgotLoading} sx={{ mt: 3, mb: 2 }}>
                    {forgotLoading ? 'Sending...' : 'Send Reset Link'}
                  </Button>
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
                  <Link component="button" variant="body2" onClick={() => { setForgotMode(false); setForgotError(''); setForgotMessage('') }} sx={{ cursor: 'pointer' }}>
                    Back to Sign In
                  </Link>
                </Typography>

              {/* Sign In Mode */}
              </>) : ssoMode ? (<>
                <Typography variant="h6" component="h2" gutterBottom>Sign in with SSO</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Enter your work email — we'll route you to your organization's identity provider.
                </Typography>
                {ssoError && <Alert severity="error" sx={{ mb: 2 }}>{ssoError}</Alert>}
                <Box component="form" onSubmit={handleSsoSubmit}>
                  <TextField fullWidth label="Work email" type="email" margin="normal" required autoFocus
                    value={ssoEmail} onChange={e => setSsoEmail(e.target.value)} />
                  <Button type="submit" fullWidth variant="contained" disabled={ssoChecking}
                    startIcon={ssoChecking ? <CircularProgress size={16} /> : <VpnKey />}
                    sx={{ mt: 3, mb: 2 }}>
                    {ssoChecking ? 'Looking up your IdP…' : 'Continue with SSO'}
                  </Button>
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
                  <Link component="button" variant="body2"
                    onClick={() => { setSsoMode(false); setSsoError('') }}
                    sx={{ cursor: 'pointer' }}>
                    Back to password sign-in
                  </Link>
                </Typography>

              </>) : (<>
                <Typography variant="h6" component="h2" gutterBottom>Sign In</Typography>
                {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
                {ssoError && <Alert severity="error" sx={{ mb: 2 }}>{ssoError}</Alert>}

                {/* SSO is the enterprise default — surface as a primary path */}
                <Button fullWidth variant="outlined" startIcon={<VpnKey />}
                  onClick={() => { setSsoMode(true); setSsoError('') }} sx={{ mb: 2 }}>
                  Sign in with SSO
                </Button>
                <Divider sx={{ my: 2 }}>
                  <Typography variant="caption" color="text.secondary">or sign in with password</Typography>
                </Divider>

                <Box component="form" onSubmit={handleSubmit}>
                  <TextField fullWidth label="Email" value={credentials.username}
                    onChange={handleChange('username')} margin="normal" required autoFocus />
                  <TextField fullWidth label="Password" type={showPassword ? 'text' : 'password'}
                    value={credentials.password} onChange={handleChange('password')}
                    margin="normal" required
                    InputProps={{ endAdornment: (
                      <InputAdornment position="end">
                        <IconButton onClick={() => setShowPassword(!showPassword)} edge="end" size="small">
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ) }}
                  />
                  <Button type="submit" fullWidth variant="contained" disabled={loading} sx={{ mt: 3, mb: 2 }}>
                    {loading ? 'Signing in...' : 'Sign In'}
                  </Button>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                  <Link component="button" variant="body2" onClick={() => setForgotMode(true)} sx={{ cursor: 'pointer' }}>
                    Forgot password?
                  </Link>
                  {onSwitchToSignup && (
                    <Link component="button" variant="body2" onClick={onSwitchToSignup} sx={{ cursor: 'pointer' }}>
                      Join your organisation
                    </Link>
                  )}
                </Box>
              </>)}
            </CardContent>
          </Card>

        </Paper>
      </Box>
    </Container>
  )
}

export default LoginPage
